import { useState, useEffect, useRef } from 'react';
import type { FutebolUltimoEvento, FutebolEventoPartida, FutebolPartidaAtiva, FutebolJogoDia, FutebolIncidente } from '@/shared/types';
import { formatarTempoPlacarTv, siglaLiga, siglaTime, chaveGolDedup, ligaLogoUrl, placarDoTimeAvancou } from '../../domain/usecases/partida';
import { useSegundosEstimados } from '../hooks/useSegundosEstimados';
import FutebolGolOverlay   from './FutebolGolOverlay';
import FutebolEventoPopup  from './FutebolEventoPopup';
import TvLivePitch         from './TvLivePitch';

function isEventoNormal(ev: FutebolEventoPartida): ev is FutebolUltimoEvento {
  return ev !== null && 'minuto' in ev;
}

const SHADOW_BUG = '0 12px 48px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.08)';

function CartaoIndicators({ amarelos, vermelhos }: { amarelos: number; vermelhos: number }) {
  if (amarelos === 0 && vermelhos === 0) return null;
  return (
    <div className="flex items-center gap-[2px]">
      {Array.from({ length: Math.min(amarelos, 3) }).map((_, i) => (
        <div key={`a${i}`} className="w-[4px] h-[6px] rounded-[1px] bg-yellow-400" />
      ))}
      {Array.from({ length: Math.min(vermelhos, 2) }).map((_, i) => (
        <div key={`r${i}`} className="w-[4px] h-[6px] rounded-[1px] bg-red-500" />
      ))}
    </div>
  );
}

function Bandeira({ src, alt, sigla }: { src: string | null; alt: string; sigla: string }) {
  const [erro, setErro] = useState(false);

  if (!src || erro) {
    return (
      <div className="w-[clamp(22px,2.2vw,36px)] h-[clamp(16px,1.6vw,26px)] rounded-[3px] bg-white/10 flex items-center justify-center text-[clamp(7px,0.55vw,10px)] font-black text-white/50">
        {sigla.slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErro(true)}
      className="w-[clamp(22px,2.2vw,36px)] h-[clamp(16px,1.6vw,26px)] object-contain drop-shadow-sm"
    />
  );
}

/** Logo da liga (BSD) com fallback pra sigla de texto quando não há liga_id ou a imagem falha. */
function LigaBadge({ ligaId, nome }: { ligaId: number | null; nome: string | null }) {
  const [erro, setErro] = useState(false);
  const src = ligaLogoUrl(ligaId);

  if (!src || erro) {
    return (
      <span className="text-[clamp(9px,0.75vw,13px)] font-black text-white leading-none tracking-tight">
        {siglaLiga(nome)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={nome ?? 'Liga'}
      onError={() => setErro(true)}
      className="w-[clamp(20px,1.8vw,28px)] h-[clamp(20px,1.8vw,28px)] object-contain"
    />
  );
}

function ScoreBox({
  valor,
  destaque,
  anima,
}: {
  valor: number;
  destaque: boolean;
  anima: boolean;
}) {
  return (
    <div
      className={`
        relative min-w-[clamp(28px,2.8vw,44px)] px-[clamp(4px,0.45vw,8px)] py-[clamp(2px,0.25vw,5px)]
        rounded-[clamp(6px,0.55vw,10px)] text-center
        font-black tabular-nums leading-none
        text-[clamp(14px,1.35vw,24px)] text-slate-900
        transition-colors duration-300
        ${destaque ? 'bg-amber-300 ring-2 ring-amber-400/60' : 'bg-[#a8f0d8]'}
        ${anima ? 'animate-futebol-score-pop' : ''}
      `}
    >
      {valor}
    </div>
  );
}

interface Props {
  isModoCaptura:      boolean;
  partida:            FutebolPartidaAtiva | null;
  jogo:               FutebolJogoDia | null;
  initialEventKey?:   string | null;
  onEventoProcessado?: (key: string) => void;
  temaCopa?:          boolean;
  exibirLivePitch?:   boolean;
}

/**
 * FutebolPlacar — score bug broadcast para a TV (fixed bottom-right).
 */
export default function FutebolPlacar({
  partida, jogo, initialEventKey, onEventoProcessado, temaCopa, exibirLivePitch,
}: Props) {
  const [goalFlash,   setGoalFlash]   = useState<'casa' | 'visitante' | null>(null);
  const [scoreAnima,  setScoreAnima]  = useState(false);
  const prevEventoKey = useRef<string | null>(initialEventKey ?? null);
  const prevPlacarRef = useRef<{ casa: number; vis: number } | null>(null);
  const onEventoProcessadoRef = useRef(onEventoProcessado);
  useEffect(() => { onEventoProcessadoRef.current = onEventoProcessado; });

  const ultimoEvNormal = isEventoNormal(partida?.ultimo_evento ?? null)
    ? partida!.ultimo_evento as FutebolUltimoEvento
    : null;

  const eventoKey = ultimoEvNormal
    ? ultimoEvNormal.tipo === 'gol' && partida
      ? chaveGolDedup(ultimoEvNormal.time, partida.placar_casa, partida.placar_visitante)
      : `${ultimoEvNormal.tipo}-${ultimoEvNormal.minuto}-${ultimoEvNormal.time}`
    : null;

  useEffect(() => {
    if (!eventoKey || eventoKey === prevEventoKey.current) return;

    if (ultimoEvNormal?.tipo === 'gol' && partida) {
      const prev = prevPlacarRef.current;
      const score = ultimoEvNormal.time === 'casa' ? partida.placar_casa : partida.placar_visitante;
      if (prev && !placarDoTimeAvancou(
        ultimoEvNormal.time, prev.casa, prev.vis, partida.placar_casa, partida.placar_visitante,
      )) return;
      if (!prev && score <= 0) return;
    }

    prevEventoKey.current = eventoKey;
    onEventoProcessadoRef.current?.(eventoKey);

    if (ultimoEvNormal?.tipo === 'gol') {
      setGoalFlash(ultimoEvNormal.time);
      setScoreAnima(true);
      const flashTimer = setTimeout(() => setGoalFlash(null),  3_000);
      const animTimer  = setTimeout(() => setScoreAnima(false), 800);
      return () => { clearTimeout(flashTimer); clearTimeout(animTimer); };
    }
    // eventoKey é a única dep de ciclo: `partida` muda a cada CDC e cancelaria o dismiss.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoKey]);

  useEffect(() => {
    if (!partida) return;
    prevPlacarRef.current = { casa: partida.placar_casa, vis: partida.placar_visitante };
  }, [partida?.placar_casa, partida?.placar_visitante]);

  const segundosEstimados = useSegundosEstimados(partida?.tempo_jogo);

  if (!partida?.fixture_id || !jogo) return null;

  const golInitialKey   = initialEventKey?.startsWith('gol-') ? initialEventKey : null;
  const popupInitialKey = initialEventKey && !initialEventKey.startsWith('gol-') ? initialEventKey : null;

  const incidents = (partida.incidents ?? []) as FutebolIncidente[];
  const cartoesCasa = {
    amarelos:  incidents.filter(i => i.type === 'card' && i.is_home && i.card_type === 'yellow').length,
    vermelhos: incidents.filter(i => i.type === 'card' && i.is_home && (i.card_type === 'red' || i.card_type === 'yellowRed')).length,
  };
  const cartoesVisit = {
    amarelos:  incidents.filter(i => i.type === 'card' && !i.is_home && i.card_type === 'yellow').length,
    vermelhos: incidents.filter(i => i.type === 'card' && !i.is_home && (i.card_type === 'red' || i.card_type === 'yellowRed')).length,
  };

  const nomeCasa      = jogo.time_casa;
  const nomeVisitante = jogo.time_visitante;
  const siglaCasa     = siglaTime(nomeCasa);
  const siglaVisit    = siglaTime(nomeVisitante);

  const emAndamento = !!(partida.tempo_jogo && partida.tempo_jogo !== 'FT')
    || jogo.status === 'em_andamento';
  const encerrado   = partida.tempo_jogo === 'FT' || jogo.status === 'finalizado';

  const isBrasil = !!temaCopa && (
    nomeCasa.toLowerCase().includes('brasil') ||
    nomeVisitante.toLowerCase().includes('brasil')
  );

  const posseCasa = partida.stats?.home.possession;
  const posseVisit = partida.stats?.away.possession;
  const casaPressiona = posseCasa != null && posseVisit != null
    ? posseCasa >= posseVisit
    : partida.momentum?.home != null && partida.momentum?.away != null
      ? partida.momentum.home >= partida.momentum.away
      : true;

  return (
    <>
      <FutebolGolOverlay
        evento={ultimoEvNormal?.tipo === 'gol' ? ultimoEvNormal : null}
        jogo={jogo}
        placarCasa={partida.placar_casa}
        placarVisitante={partida.placar_visitante}
        initialKey={golInitialKey}
        temaCopa={temaCopa}
      />
      <FutebolEventoPopup
        evento={ultimoEvNormal?.tipo !== 'gol' ? ultimoEvNormal : null}
        jogo={jogo}
        initialKey={popupInitialKey}
      />

      <div className="fixed z-20 select-none pointer-events-none bottom-[2vw] right-[2vw] flex flex-col items-end gap-[0.5vw]">
        {exibirLivePitch && (
          <TvLivePitch
            fixtureId={partida.fixture_id}
            fallbackPitch={partida.livedata_pitch ?? null}
          />
        )}

        <div
          className="relative flex items-stretch overflow-hidden rounded-full"
          style={{ boxShadow: SHADOW_BUG }}
        >
          {/* Faixa decorativa inferior */}
          <div
            className="absolute bottom-0 left-0 right-0 h-[3px] pointer-events-none"
            style={{
              background: isBrasil
                ? 'linear-gradient(90deg, #009c3b 0%, #ffdf00 50%, #002776 100%)'
                : temaCopa
                  ? 'linear-gradient(90deg, #f97316, #ef4444, #22c55e, #3b82f6, #a855f7)'
                  : 'linear-gradient(90deg, rgba(249,115,22,0.9), rgba(251,146,60,0.5))',
            }}
          />

          {/* Timer */}
          <div className="relative flex flex-col items-center justify-center bg-white px-[clamp(10px,1vw,18px)] py-[clamp(6px,0.55vw,10px)] min-w-[clamp(52px,5vw,76px)]">
            {emAndamento && (
              <span className="absolute top-[clamp(4px,0.35vw,7px)] right-[clamp(4px,0.35vw,7px)] w-[clamp(5px,0.45vw,8px)] h-[clamp(5px,0.45vw,8px)] rounded-full bg-red-500 animate-pulse" />
            )}
            <span className="font-black tabular-nums text-slate-900 text-[clamp(13px,1.15vw,20px)] leading-none tracking-tight">
              {formatarTempoPlacarTv(partida.tempo_jogo, segundosEstimados)}
            </span>
            {emAndamento ? (
              <span className="mt-[2px] text-[clamp(6px,0.5vw,9px)] font-bold uppercase tracking-[0.14em] text-red-600">
                Ao vivo
              </span>
            ) : encerrado ? (
              <span className="mt-[2px] text-[clamp(6px,0.5vw,9px)] font-bold uppercase tracking-[0.1em] text-slate-500">
                Fim
              </span>
            ) : null}
          </div>

          {/* Corpo escuro */}
          <div
            className={`
              flex items-center gap-[clamp(6px,0.65vw,12px)] px-[clamp(10px,1vw,16px)] py-[clamp(6px,0.55vw,10px)]
              bg-[linear-gradient(180deg,rgba(8,8,10,0.97)_0%,rgba(16,16,20,0.95)_100%)]
              backdrop-blur-xl
              ${isBrasil ? 'ring-1 ring-inset ring-green-500/40' : 'ring-1 ring-inset ring-white/[0.06]'}
            `}
          >
            {/* Casa */}
            <div className="flex items-center gap-[clamp(4px,0.4vw,8px)]">
              <Bandeira src={jogo.escudo_casa} alt={nomeCasa} sigla={siglaCasa} />
              <span
                className={`w-[clamp(6px,0.5vw,9px)] h-[clamp(6px,0.5vw,9px)] rounded-full shrink-0 transition-colors ${casaPressiona && emAndamento ? 'bg-red-500' : 'bg-white/20'}`}
              />
              <div className="flex flex-col items-start gap-[1px]">
                <span className={`font-black uppercase tracking-wide text-[clamp(10px,0.85vw,15px)] leading-none ${goalFlash === 'casa' ? 'text-amber-400' : 'text-white'}`}>
                  {siglaCasa}
                </span>
                <CartaoIndicators amarelos={cartoesCasa.amarelos} vermelhos={cartoesCasa.vermelhos} />
              </div>
              <ScoreBox
                valor={partida.placar_casa}
                destaque={goalFlash === 'casa'}
                anima={scoreAnima && goalFlash === 'casa'}
              />
            </div>

            {/* Centro — competição */}
            <div className="flex flex-col items-center justify-center px-[clamp(2px,0.25vw,6px)] min-w-[clamp(28px,2.5vw,40px)]">
              <div className="rounded-full bg-black ring-1 ring-white/15 px-[clamp(5px,0.45vw,8px)] py-[clamp(4px,0.35vw,7px)] flex flex-col items-center">
                <LigaBadge ligaId={jogo.liga_id} nome={jogo.liga_nome} />
                {temaCopa && (
                  <span className="text-[clamp(6px,0.45vw,8px)] font-bold text-amber-400/90 mt-[1px]">COPA</span>
                )}
              </div>
            </div>

            {/* Visitante */}
            <div className="flex items-center gap-[clamp(4px,0.4vw,8px)]">
              <ScoreBox
                valor={partida.placar_visitante}
                destaque={goalFlash === 'visitante'}
                anima={scoreAnima && goalFlash === 'visitante'}
              />
              <div className="flex flex-col items-end gap-[1px]">
                <span className={`font-black uppercase tracking-wide text-[clamp(10px,0.85vw,15px)] leading-none ${goalFlash === 'visitante' ? 'text-amber-400' : 'text-white'}`}>
                  {siglaVisit}
                </span>
                <CartaoIndicators amarelos={cartoesVisit.amarelos} vermelhos={cartoesVisit.vermelhos} />
              </div>
              <span
                className={`w-[clamp(6px,0.5vw,9px)] h-[clamp(6px,0.5vw,9px)] rounded-full shrink-0 transition-colors ${!casaPressiona && emAndamento ? 'bg-sky-400' : 'bg-white/20'}`}
              />
              <Bandeira src={jogo.escudo_visitante} alt={nomeVisitante} sigla={siglaVisit} />
            </div>
          </div>
        </div>

        {/* Stats compactos — linha fina abaixo do bug */}
        {partida.stats && (
          <div className="flex items-center gap-[clamp(8px,0.8vw,14px)] px-[clamp(10px,0.9vw,14px)] py-[clamp(3px,0.3vw,5px)] rounded-full bg-black/70 backdrop-blur-md ring-1 ring-white/[0.06]">
            <div className="flex items-center gap-[0.35vw] min-w-[clamp(60px,6vw,90px)]">
              <span className="text-[clamp(7px,0.55vw,10px)] font-bold text-slate-300 tabular-nums w-[2ch] text-right">
                {partida.stats.home.possession}%
              </span>
              <div className="flex-1 h-[2px] rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-sky-400/70 transition-[width] duration-700"
                  style={{ width: `${partida.stats.home.possession}%` }}
                />
              </div>
              <span className="text-[clamp(7px,0.55vw,10px)] font-bold text-slate-300 tabular-nums w-[2ch]">
                {partida.stats.away.possession}%
              </span>
            </div>
            {partida.stats.home.shots_on_target != null && (
              <span className="text-[clamp(7px,0.55vw,10px)] text-white/35 font-semibold tabular-nums">
                {partida.stats.home.shots_on_target}–{partida.stats.away.shots_on_target} chutes
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
}
